import {useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {Building2, Pencil, Plus, Trash2, UserCog, Wallet} from 'lucide-react'
import {PageHeader} from '../components/PageHeader'
import {Button} from '../components/ui/Button'
import {Card} from '../components/ui/Card'
import {Badge} from '../components/ui/Badge'
import {BudgetBar} from '../components/BudgetBar'
import {CardSkeletonGrid, EmptyState} from '../components/ui/States'
import {Modal, ConfirmDialog} from '../components/ui/Modal'
import {Field, Input, Select} from '../components/ui/Field'
import {ModelAllowlist} from '../components/ModelAllowlist'
import {budgetService, BudgetError} from '../services/budgetService'
import {accessUsersService} from '../services/accessUsersService'
import {modelsService} from '../services/modelsService'
import {usageService, type UsageBucket} from '../services/usageService'
import {toast} from '../store/uiStore'
import {useModelsStore} from '../store/modelsStore'
import {formatBRL, uid, nowIso} from '../lib/utils'
import {agentStudioPath} from '../lib/routes'
import type {Area, Person, StudioAccessUser} from '../types'
import {AGENT_STUDIO_PERMISSIONS as P, useAgentStudioPermissions} from '../lib/permissions'

export function BudgetPage() {
  const navigate = useNavigate()
  const {has} = useAgentStudioPermissions()
  const canManage = has(P.budgetManage)
  const canManageUsers = has(P.usersManage)
  const [areas, setAreas] = useState<Area[]>([])
  const [people, setPeople] = useState<Person[]>([])
  const [accessUsers, setAccessUsers] = useState<StudioAccessUser[]>([])
  const [spentByArea, setSpentByArea] = useState<Record<string, number>>({})
  const [spentByPerson, setSpentByPerson] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [areaForm, setAreaForm] = useState<Area | null>(null)
  const [deleteArea, setDeleteArea] = useState<Area | null>(null)

  const catalog = useModelsStore((state) => state.catalog)
  const ensureModels = useModelsStore((state) => state.load)
  const reloadAllowed = useModelsStore((state) => state.reloadAllowed)
  const [areaModelIds, setAreaModelIds] = useState<string[]>([])

  const load = useCallback(async () => {
    try {
      const [allAreas, allPeople, summary, authorizedUsers] = await Promise.all([
        budgetService.listAreas(),
        budgetService.listPeople(),
        usageService.summary().catch(() => null),
        canManage ? accessUsersService.list() : Promise.resolve([]),
      ])
      const areaSpend: Record<string, number> = {}
      const personSpend: Record<string, number> = {}
      if (summary) {
        Object.entries(summary.byArea as Record<string, UsageBucket>).forEach(([id, value]) => {
          areaSpend[id] = value.costUsd
        })
        Object.entries(summary.byPerson as Record<string, UsageBucket>).forEach(([id, value]) => {
          personSpend[id] = value.costUsd
        })
      }
      setAreas(allAreas)
      setPeople(allPeople)
      setAccessUsers(authorizedUsers)
      setSpentByArea(areaSpend)
      setSpentByPerson(personSpend)
    } catch {
      toast.error('Erro ao carregar áreas e budgets.')
    } finally {
      setLoading(false)
    }
  }, [canManage])

  useEffect(() => {
    load()
    ensureModels()
  }, [ensureModels, load])

  const allCatalogIds = useMemo(() => catalog.map((model) => model.id), [catalog])

  useEffect(() => {
    if (!areaForm) return
    const isExisting = areas.some((area) => area.id === areaForm.id)
    if (isExisting) {
      modelsService
        .getAreaModels(areaForm.id)
        .then(setAreaModelIds)
        .catch(() => setAreaModelIds([]))
    } else {
      setAreaModelIds(allCatalogIds)
    }
  }, [allCatalogIds, areaForm, areas])

  const peopleByArea = useMemo(() => {
    const grouped: Record<string, Person[]> = {}
    people.forEach((person) => {
      ;(grouped[person.areaId] ??= []).push(person)
    })
    return grouped
  }, [people])

  const accessUserByGuid = useMemo(
    () => new Map(accessUsers.map((user) => [user.guid, user])),
    [accessUsers]
  )

  const totals = useMemo(
    () => ({
      totalBudget: areas.reduce((total, area) => total + area.budgetBRL, 0),
      totalAllocated: people.reduce((total, person) => total + person.budgetBRL, 0),
    }),
    [areas, people]
  )

  async function saveArea(area: Area) {
    try {
      await budgetService.saveArea(area)
      await modelsService.setAreaModels(area.id, areaModelIds)
      setAreaForm(null)
      toast.success('Área salva.')
      await load()
      reloadAllowed()
    } catch (error) {
      toast.error(error instanceof BudgetError ? error.message : 'Erro ao salvar área.')
    }
  }

  async function confirmDeleteArea() {
    if (!deleteArea) return
    try {
      await budgetService.removeArea(deleteArea.id)
      toast.success('Área excluída.')
      await load()
    } catch (error) {
      toast.error(error instanceof BudgetError ? error.message : 'Erro ao excluir área.')
    }
  }

  const newArea = () =>
    setAreaForm({
      id: uid('area'),
      name: '',
      budgetBRL: 0,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    })

  return (
    <>
      <PageHeader
        title='Áreas & Budget'
        subtitle={`Budget total ${formatBRL(totals.totalBudget)} · alocado ${formatBRL(
          totals.totalAllocated
        )} · gasto no mês ${formatBRL(Object.values(spentByArea).reduce((a, b) => a + b, 0))}`}
        actions={
          canManage ? (
            <div className='flex items-center gap-2'>
              {canManageUsers && (
                <Button
                  variant='secondary'
                  icon={UserCog}
                  onClick={() => navigate(agentStudioPath('users'))}
                >
                  Gerenciar usuários
                </Button>
              )}
              <Button variant='primary' icon={Plus} onClick={newArea}>
                Nova área
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className='flex-1 overflow-y-auto p-8 scrollbar-thin'>
        {loading ? (
          <CardSkeletonGrid count={3} />
        ) : areas.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title='Nenhuma área ainda'
            description='Crie áreas, defina o budget e escolha um administrador entre os usuários autorizados.'
            action={
              canManage ? (
                <Button variant='primary' icon={Plus} onClick={newArea}>
                  Nova área
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className='grid grid-cols-1 gap-5 xl:grid-cols-2'>
            {areas.map((area) => {
              const members = peopleByArea[area.id] ?? []
              const usage = budgetService.areaUsage(area, people)
              const admin = area.adminUserGuid
                ? accessUserByGuid.get(area.adminUserGuid)
                : undefined
              return (
                <Card key={area.id} className='flex flex-col p-5'>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='flex items-center gap-3'>
                      <div className='flex h-10 w-10 items-center justify-center rounded-xl bg-navy/10 text-navy'>
                        <Building2 className='h-5 w-5' />
                      </div>
                      <div>
                        <h3 className='text-sm font-semibold text-ink'>{area.name}</h3>
                        <p className='text-xs text-slate'>{members.length} pessoa(s)</p>
                        <p className='mt-0.5 text-xs text-slate'>
                          Admin:{' '}
                          <span className='font-medium text-ink'>
                            {admin?.name || 'não definido'}
                          </span>
                        </p>
                      </div>
                    </div>
                    {canManage && (
                      <div className='flex gap-1'>
                        <button
                          onClick={() => setAreaForm(area)}
                          className='rounded-lg p-1.5 text-slate hover:bg-cloud hover:text-ink'
                          aria-label='Editar área'
                        >
                          <Pencil className='h-4 w-4' />
                        </button>
                        <button
                          onClick={() => setDeleteArea(area)}
                          className='rounded-lg p-1.5 text-slate hover:bg-danger/10 hover:text-danger'
                          aria-label='Excluir área'
                        >
                          <Trash2 className='h-4 w-4' />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className='mt-4 space-y-3'>
                    <BudgetBar
                      used={usage.allocated}
                      total={usage.budget}
                      label='Alocado aos usuários'
                    />
                    <BudgetBar
                      used={spentByArea[area.id] ?? 0}
                      total={usage.budget}
                      label='Gasto no mês (real)'
                    />
                  </div>

                  <div className='mt-4 space-y-2'>
                    {members.length === 0 ? (
                      <p className='rounded-lg bg-cloud px-3 py-2 text-xs text-slate'>
                        Nenhum usuário configurado nesta área.
                      </p>
                    ) : (
                      members.map((person) => (
                        <div key={person.id} className='rounded-xl border border-line p-3'>
                          <div className='flex items-start justify-between gap-2'>
                            <div className='min-w-0'>
                              <p className='truncate text-sm font-medium text-ink'>{person.name}</p>
                              <p className='truncate text-xs text-slate'>
                                {person.role || person.email}
                              </p>
                            </div>
                            <Badge tone='neutral'>{formatBRL(person.budgetBRL)}</Badge>
                          </div>
                          <div className='mt-2'>
                            <BudgetBar
                              used={spentByPerson[person.id] ?? 0}
                              total={person.budgetBRL}
                              label='Gasto no mês (real)'
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </div>

      <Modal
        open={!!areaForm}
        onClose={() => setAreaForm(null)}
        title={
          areaForm && areas.some((area) => area.id === areaForm.id) ? 'Editar área' : 'Nova área'
        }
        footer={
          <>
            <Button variant='ghost' onClick={() => setAreaForm(null)}>
              Cancelar
            </Button>
            <Button
              variant='primary'
              onClick={() => areaForm && saveArea(areaForm)}
              disabled={!areaForm?.name}
            >
              Salvar
            </Button>
          </>
        }
      >
        {areaForm && (
          <>
            <Field label='Nome da área'>
              <Input
                value={areaForm.name}
                onChange={(event) => setAreaForm({...areaForm, name: event.target.value})}
                placeholder='ex.: Atendimento'
              />
            </Field>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <Field label='Budget total mensal (US$)' className='mb-0'>
                <Input
                  type='number'
                  min={0}
                  step='10'
                  value={areaForm.budgetBRL}
                  onChange={(event) =>
                    setAreaForm({...areaForm, budgetBRL: Math.max(0, Number(event.target.value))})
                  }
                />
              </Field>
              <Field
                label='Admin da área'
                hint='somente usuários com acesso ao Agent Studio'
                className='mb-0'
              >
                <Select
                  value={areaForm.adminUserGuid ?? ''}
                  onChange={(event) =>
                    setAreaForm({...areaForm, adminUserGuid: event.target.value || undefined})
                  }
                >
                  <option value=''>— ninguém —</option>
                  {accessUsers.map((user) => (
                    <option key={user.guid} value={user.guid}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <ModelAllowlist
              label='Modelos liberados para a área'
              hint='define o universo disponível para os usuários desta área'
              models={catalog}
              selected={areaModelIds}
              onChange={setAreaModelIds}
            />
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deleteArea}
        onClose={() => setDeleteArea(null)}
        onConfirm={confirmDeleteArea}
        title='Excluir área'
        message={`Excluir a área "${deleteArea?.name}"? Ela precisa estar sem usuários vinculados.`}
      />
    </>
  )
}
